import { useMemo, useState } from 'react'
import { Send, Zap, Mail, AlertCircle, CheckCircle2, XCircle } from 'lucide-react'
import { useMailboxes } from '@hooks/useMailboxes'
import { mailboxService, InstantSendResult } from '@services/mailbox.service'
import { Mailbox } from '@/types'

/**
 * Instant Send Page
 *
 * One-off bulk send with full analytics (opens/clicks tracked).
 * Sends each recipient through the standard email pipeline so
 * EmailLog + tracking pixel + click rewriting all apply.
 */
export function InstantSendPage() {
  const { data: mailboxesPage, isLoading: loadingMailboxes } = useMailboxes({ pageSize: 100 })

  const activeMailboxes = useMemo<Mailbox[]>(
    () => (mailboxesPage?.data || []).filter((m) => m.status === 'ACTIVE'),
    [mailboxesPage]
  )

  const [mailboxId, setMailboxId] = useState<number | ''>('')
  const [recipientsText, setRecipientsText] = useState('')
  const [subject, setSubject] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [textBody, setTextBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<InstantSendResult | null>(null)

  // Default the mailbox dropdown to the first active mailbox once loaded.
  useMemo(() => {
    if (mailboxId === '' && activeMailboxes.length === 1) {
      setMailboxId(activeMailboxes[0].id)
    }
  }, [activeMailboxes, mailboxId])

  const parsedRecipients = useMemo(() => {
    return recipientsText
      .split(/[\n,;]+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        // Support "Name <email@x.com>" or "email@x.com,Name"
        const angle = line.match(/^(.*?)\s*<([^>]+)>\s*$/)
        if (angle) return { name: angle[1].trim() || undefined, email: angle[2].trim() }
        const comma = line.split(',').map((s) => s.trim()).filter(Boolean)
        if (comma.length >= 2 && /@/.test(comma[0])) return { email: comma[0], name: comma[1] }
        return { email: line }
      })
      .filter((r) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email))
  }, [recipientsText])

  const canSubmit =
    !!mailboxId &&
    parsedRecipients.length > 0 &&
    subject.trim().length > 0 &&
    htmlBody.trim().length > 0 &&
    !submitting

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit || typeof mailboxId !== 'number') return

    setSubmitting(true)
    setError(null)
    setResult(null)

    try {
      const res = await mailboxService.instantSend(mailboxId, {
        recipients: parsedRecipients,
        subject: subject.trim(),
        htmlBody,
        textBody: textBody.trim() ? textBody : undefined,
      })
      setResult(res)
    } catch (err) {
      const e = err as { response?: { data?: { error?: string; errors?: Record<string, string[]> } }; message?: string }
      const apiErr = e.response?.data
      if (apiErr?.errors) {
        const lines = Object.entries(apiErr.errors).map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`)
        setError(lines.join('\n'))
      } else {
        setError(apiErr?.error || e.message || 'Send failed')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="icon-container icon-container-lg">
          <Zap size={24} className="text-violet-500" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Instant Send</h1>
          <p className="text-sm text-slate-400">
            {/* mm-tracking-split-20260814:copy */}
            Bulk one-off send with open tracking. Each email goes through the standard
            pipeline — see results in <span className="text-violet-400">Tracking</span> &amp;{' '}
            <span className="text-violet-400">Dashboard</span>.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">From</h2>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Mailbox *</label>
            <select
              className="input w-full"
              value={mailboxId}
              onChange={(e) => setMailboxId(e.target.value ? Number(e.target.value) : '')}
              disabled={loadingMailboxes}
              required
            >
              <option value="">-- Select active mailbox --</option>
              {activeMailboxes.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.email}) — {m.sentToday}/{m.dailyLimit} today
                </option>
              ))}
            </select>
            {!loadingMailboxes && activeMailboxes.length === 0 && (
              <p className="text-xs text-amber-400 mt-1">No ACTIVE mailboxes. Add or activate one first.</p>
            )}
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">Recipients</h2>
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              One email per line ({parsedRecipients.length} valid)
            </label>
            <textarea
              className="input w-full font-mono text-sm"
              rows={8}
              placeholder={`john@example.com\nJane Doe <jane@example.com>\nmark@example.com, Mark Smith`}
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">
              Formats: <code>email</code>, <code>Name &lt;email&gt;</code>, or <code>email, Name</code>. Separators:
              newline, comma, or semicolon. Max 500 per batch.
            </p>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">Message</h2>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Subject *</label>
            <input
              type="text"
              className="input w-full"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={500}
              required
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              HTML body * <span className="text-slate-500">(tracking pixel injected automatically; links are not rewritten)</span>
            </label>
            <textarea
              className="input w-full font-mono text-xs"
              rows={10}
              placeholder={`<p>Hi,</p>\n<p>Quick note...</p>\n<p>Best,<br/>Team</p>`}
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              required
            />
          </div>
          <details>
            <summary className="text-sm text-slate-400 cursor-pointer">Plain-text fallback (optional)</summary>
            <textarea
              className="input w-full font-mono text-xs mt-2"
              rows={6}
              placeholder="If empty, auto-generated from HTML."
              value={textBody}
              onChange={(e) => setTextBody(e.target.value)}
            />
          </details>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-slate-500 flex items-center gap-2">
            <Mail size={14} /> Sends throttled 500ms between recipients to protect deliverability.
          </p>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={!canSubmit}>
            <Send size={16} />
            {submitting ? 'Sending…' : `Send to ${parsedRecipients.length}`}
          </button>
        </div>
      </form>

      {error && (
        <div className="card p-4 border border-red-500/40 bg-red-500/5">
          <div className="flex items-start gap-2 text-red-400">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <div className="whitespace-pre-wrap text-sm">{error}</div>
          </div>
        </div>
      )}

      {result && (
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-100">Results</h2>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-400">Total: {result.total}</span>
              <span className="text-emerald-400 flex items-center gap-1">
                <CheckCircle2 size={14} /> Sent: {result.sent}
              </span>
              <span className="text-red-400 flex items-center gap-1">
                <XCircle size={14} /> Failed: {result.failed}
              </span>
            </div>
          </div>
          <div className="overflow-auto max-h-96 border border-slate-700/50 rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/50 sticky top-0">
                <tr>
                  <th className="text-left p-2 text-slate-400">Email</th>
                  <th className="text-left p-2 text-slate-400">Status</th>
                  <th className="text-left p-2 text-slate-400">Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((r, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="p-2 text-slate-300">{r.email}</td>
                    <td className="p-2">
                      {r.status === 'SENT' ? (
                        <span className="text-emerald-400">SENT</span>
                      ) : (
                        <span className="text-red-400">FAILED</span>
                      )}
                    </td>
                    <td className="p-2 text-slate-500 text-xs">{r.error || r.messageId || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default InstantSendPage
